import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'pp-admin-theme';
const DEFAULT_THEME = 'dark';

/** Cycle order for the top-bar toggle. */
export const ADMIN_THEMES = ['dark', 'navy', 'light'];

export const ADMIN_THEME_LABELS = {
  dark: 'Dark',
  navy: 'Navy',
  light: 'Light',
};

/* Painted behind the admin canvas so overscroll and the area under the sticky
   bar match. Kept in sync with --adm-bg-0 in src/styles/admin-theme.css. */
const CANVAS_BG = {
  dark: 'oklch(0.145 0.012 250)',
  navy: 'oklch(0.155 0.038 262)',
  light: 'oklch(0.975 0.004 250)',
};

const readStoredTheme = () => {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return ADMIN_THEMES.includes(saved) ? saved : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

/**
 * Theme state for the admin surface.
 *
 * The value is read synchronously in the state initialiser so the wrapper
 * renders with the correct `data-theme` on its very first paint — no flash.
 * Only presentation is affected; nothing here touches auth or session state.
 */
export const useAdminTheme = () => {
  const [theme, setTheme] = useState(readStoredTheme);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode / storage disabled — the session still themes correctly */
    }

    const previousBodyBg = document.body.style.backgroundColor;
    const previousRootScheme = document.documentElement.style.colorScheme;
    document.body.style.backgroundColor = CANVAS_BG[theme] || CANVAS_BG[DEFAULT_THEME];
    document.documentElement.style.colorScheme = theme === 'light' ? 'light' : 'dark';

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
      document.documentElement.style.colorScheme = previousRootScheme;
    };
  }, [theme]);

  const cycleTheme = useCallback(
    () =>
      setTheme((current) => {
        const i = ADMIN_THEMES.indexOf(current);
        return ADMIN_THEMES[(i + 1) % ADMIN_THEMES.length];
      }),
    []
  );

  return { theme, setTheme, cycleTheme, nextTheme: ADMIN_THEMES[(ADMIN_THEMES.indexOf(theme) + 1) % ADMIN_THEMES.length] };
};

export default useAdminTheme;
